import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { connectOwnWhatsAppApp } from '../../services/tenant.service';

const label = 'block text-sm font-medium text-gray-700';
const input = 'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono';

const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

/**
 * For a business that already runs WhatsApp Cloud API in its own Meta app and
 * will not hand its WhatsApp account to a provider. They paste what identifies
 * their number, and use a dedicated app callback. Existing integrations must
 * not be overwritten; shared-app routing needs a separate supported setup.
 */
const ConnectOwnApp = ({ tenantId, onConnected, connection = null }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [callbackUse, setCallbackUse] = useState('');
  const [fields, setFields] = useState({
    phone_number_id: connection?.phone_number_id || '', waba_id: connection?.waba_id || '', access_token: '', app_secret: '',
  });

  const set = (name) => (event) => setFields((prev) => ({ ...prev, [name]: event.target.value }));
  const ready = fields.phone_number_id.trim() && fields.waba_id.trim()
    && fields.access_token.trim() && fields.app_secret.trim() && callbackUse === 'dedicated';

  const submit = async (event) => {
    event.preventDefault();
    if (!ready || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    try {
      const next = await connectOwnWhatsAppApp(tenantId, {
        phone_number_id: fields.phone_number_id.trim(),
        waba_id: fields.waba_id.trim(),
        access_token: fields.access_token.trim(),
        app_secret: fields.app_secret.trim(),
        dedicated_app_confirmed: true,
      });
      toast.success('WhatsApp connected. Add the webhook URL in your Meta app to start receiving messages.');
      setFields({
        phone_number_id: next?.connection?.phone_number_id || fields.phone_number_id,
        waba_id: next?.connection?.waba_id || fields.waba_id,
        access_token: '', app_secret: '',
      });
      setCallbackUse('');
      setOpen(false);
      onConnected?.(next);
    } catch (err) {
      toast.error(errorText(err, 'Could not connect that number. Check the IDs and the token.'));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="text-sm font-medium text-indigo-600 hover:underline">
        {connection ? 'Update credentials' : 'I already run WhatsApp Cloud API in my own Meta app'}
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Connect your own Cloud API number</h3>
        <p className="mt-1 text-sm text-gray-600">
          Your number stays registered to you. Incoming messages must be sent to Serviti using a
          dedicated Meta app callback. Do not replace a webhook used by another integration or number.
        </p>
      </div>

      <div>
        <label htmlFor="own-pnid" className={label}>Phone number ID</label>
        <input id="own-pnid" value={fields.phone_number_id} onChange={set('phone_number_id')}
          placeholder="1226267603912605" className={input} autoComplete="off" readOnly={Boolean(connection)} />
      </div>
      <div>
        <label htmlFor="own-waba" className={label}>WhatsApp Business Account ID</label>
        <input id="own-waba" value={fields.waba_id} onChange={set('waba_id')}
          placeholder="901684869169238" className={input} autoComplete="off" readOnly={Boolean(connection)} />
      </div>
      <div>
        <label htmlFor="own-token" className={label}>Permanent access token</label>
        <input id="own-token" type="password" value={fields.access_token} onChange={set('access_token')}
          className={input} autoComplete="off" />
        <p className="mt-1 text-xs text-gray-500">
          A System User token with <span className="font-mono">whatsapp_business_messaging</span> and
          <span className="font-mono"> whatsapp_business_management</span>. It is
          encrypted and never shown again.
        </p>
      </div>
      <div>
        <label htmlFor="own-secret" className={label}>App Secret (required)</label>
        <input id="own-secret" type="password" value={fields.app_secret} onChange={set('app_secret')}
          className={input} autoComplete="off" />
        <p className="mt-1 text-xs text-gray-500">
          Required to verify that incoming messages came from your Meta app. Re-enter it when updating credentials.
        </p>
      </div>

      <div>
        <label htmlFor="own-callback-use" className={label}>Does this app callback serve another integration or number?</label>
        <select id="own-callback-use" value={callbackUse} onChange={(event) => setCallbackUse(event.target.value)} className={input}>
          <option value="">Choose before continuing</option>
          <option value="dedicated">No — this callback is dedicated to this number and Serviti</option>
          <option value="shared">Yes, or I am not sure</option>
        </select>
        {callbackUse === 'shared' && (
          <p role="alert" className="mt-2 text-sm text-amber-900">
            Stop here: changing that callback could disconnect your existing system or other numbers.
            Contact Serviti support to arrange shared-app routing. Do not overwrite the existing callback.
          </p>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button type="submit" disabled={busy || !ready}
          className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50">
          {busy ? 'Checking with Meta…' : connection ? 'Save credentials' : 'Connect'}
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy}
          className="text-sm text-gray-600 hover:underline">
          Cancel
        </button>
      </div>
    </form>
  );
};

/** Shown after connecting: the one thing they still have to do at Meta. */
export const OwnAppWebhook = ({ url, verifyToken }) => {
  // Compatibility with an older API response; the handshake always expects
  // the suffix, never the complete URL.
  const token = verifyToken || (url ? url.split('/').pop() : '');
  const copy = async (value, field) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${field} copied.`);
    } catch {
      toast('Copy it from the box above.');
    }
  };

  if (!url) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        This number delivers to its own webhook URL, but the API has no address configured
        (API_BASE_URL), so it can&rsquo;t be shown. Ask Serviti support for it.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-2">
      <p className="text-sm font-semibold text-blue-900">One step left, in your own Meta app</p>
      <p className="text-sm text-blue-900">
        Only on the dedicated app: under WhatsApp &rarr; Configuration, copy each value into its matching
        field and subscribe to <span className="font-mono">messages</span>.
      </p>
      {[['Callback URL', url], ['Verify token', token]].map(([field, value]) => (
        <div key={field}>
          <p className="mb-1 text-xs font-semibold text-blue-900">{field}</p>
          <div className="flex items-start gap-2">
        <code className="min-w-0 flex-1 break-all rounded bg-white px-2 py-1.5 text-xs text-gray-800">{value}</code>
        <button type="button" aria-label={`Copy ${field}`} onClick={() => copy(value, field)}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-blue-300 bg-white px-2 py-1.5 text-xs font-medium text-blue-800">
          <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" /> Copy
        </button>
          </div>
      </div>
      ))}
      <p className="text-xs text-blue-800">
        Keep these values private. Every delivery also requires a valid signature from your App Secret.
      </p>
    </div>
  );
};

export default ConnectOwnApp;
